import type { DataState } from "./types";
import type { TabValue } from "./config";
import type { DateRange } from "@lemmonhq/uikit";
import type { ReservationStatus, RequestStatus } from "./components/PageHeader";
import dayjs from "dayjs";
import type { useTrpc } from "@app/vue/providers/TrpcProvider";
import {
  listReservationRequests,
  listReservations,
} from "../../utils/trpcReservations";

type TrpcClient = ReturnType<typeof useTrpc>;

interface FetchParams {
  tab: TabValue;
  searchQuery: string;
  dateRange: DateRange;
  reservationStatus: ReservationStatus;
  requestStatus: RequestStatus;
}

export async function fetchData(
  trpc: TrpcClient,
  state: DataState,
  _signal: AbortSignal | undefined,
  params: FetchParams,
): Promise<void> {
  try {
    state.loading = true;
    state.error = "";
    state.pagination.offset = 0;
    state.pagination.hasMore = true;

    // Convert date range to ISO strings with proper time boundaries
    const startDate = dayjs(params.dateRange.startDate)
      .startOf("day")
      .toISOString();
    const endDate = dayjs(params.dateRange.endDate).endOf("day").toISOString();

    if (params.tab === "reservations") {
      const result = await listReservations(trpc, {
        date_from: startDate,
        date_to: endDate,
        party_name: params.searchQuery || undefined,
        limit: state.pagination.limit.toString(),
        offset: "0",
        sort: "time",
        order: "desc", // Latest first
        status:
          params.reservationStatus !== "all"
            ? params.reservationStatus
            : undefined,
      });

      state.reservations = (result as any).data || [];
      state.requests = [];
      state.pagination.hasMore =
        ((result as any).data?.length || 0) >= state.pagination.limit;
    } else {
      const result = await listReservationRequests(trpc, {
        date_from: startDate,
        date_to: endDate,
        date_field: "updated_at",
        party_name: params.searchQuery || undefined,
        limit: state.pagination.limit.toString(),
        offset: "0",
        status:
          params.requestStatus !== "all" ? params.requestStatus : undefined,
        sort: "requested_time",
        order: "desc", // Latest first
      });

      state.requests = (result as any).data || [];
      state.reservations = [];
      state.pagination.hasMore =
        ((result as any).data?.length || 0) >= state.pagination.limit;
    }

    state.pagination.offset = state.pagination.limit;
  } catch (e: any) {
    if (e.name === "AbortError") {
      // Request was aborted, don't update error state
      return;
    }
    console.error("Failed to load data:", e);
    state.error = e.message || "Failed to load data";
    state.reservations = [];
    state.requests = [];
  } finally {
    state.loading = false;
  }
}

export async function loadMore(
  trpc: TrpcClient,
  state: DataState,
  _signal: AbortSignal | undefined,
  params: FetchParams,
): Promise<void> {
  if (state.loadingMore || !state.pagination.hasMore) return;

  try {
    state.loadingMore = true;

    // Convert date range to ISO strings with proper time boundaries
    const startDate = dayjs(params.dateRange.startDate)
      .startOf("day")
      .toISOString();
    const endDate = dayjs(params.dateRange.endDate).endOf("day").toISOString();

    if (params.tab === "reservations") {
      const result = await listReservations(trpc, {
        date_from: startDate,
        date_to: endDate,
        party_name: params.searchQuery || undefined,
        limit: state.pagination.limit.toString(),
        offset: state.pagination.offset.toString(),
        sort: "time",
        order: "desc", // Latest first
        status:
          params.reservationStatus !== "all"
            ? params.reservationStatus
            : undefined,
      });

      const newData = (result as any).data || [];
      state.reservations = [...state.reservations, ...newData];
      state.pagination.hasMore = newData.length >= state.pagination.limit;
      state.pagination.offset += newData.length;
    } else {
      const result = await listReservationRequests(trpc, {
        date_from: startDate,
        date_to: endDate,
        date_field: "updated_at",
        party_name: params.searchQuery || undefined,
        limit: state.pagination.limit.toString(),
        offset: state.pagination.offset.toString(),
        status:
          params.requestStatus !== "all" ? params.requestStatus : undefined,
        sort: "requested_time",
        order: "desc", // Latest first
      });

      const newData = (result as any).data || [];
      state.requests = [...state.requests, ...newData];
      state.pagination.hasMore = newData.length >= state.pagination.limit;
      state.pagination.offset += newData.length;
    }
  } catch (e: any) {
    if (e.name === "AbortError") {
      return;
    }
    console.error("Failed to load more data:", e);
  } finally {
    state.loadingMore = false;
  }
}
